import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { InfiniteScroll, PageResponse } from "../lib/pagination";
import { useAsyncData } from "./useAsyncData";
import { useAsyncHandler } from "./useAsyncHandler";

interface UseAsyncInfiniteDataOptions {
  /** Items asked for with each request. */
  pageSize?: number;
  /** When it changes after mount (a search query, say), the list loads again from the start. */
  key?: string;
}

export interface UseAsyncInfiniteDataResult<T> {
  /** Every item loaded so far, in order. */
  items: T[];
  /** True while the first page loads. */
  loading: boolean;
  /** The message of a failed first page. */
  error: string | null;
  /** Clears the list and loads it again from the start. */
  refresh: () => void;
  /** Hand to the list component as it is. */
  scroll: InfiniteScroll;
}

/**
 * Loads an offset-paged list for a list that scrolls without end: the first page through
 * `useAsyncData`, every further page through `useAsyncHandler`, appended to what is there. The
 * counterpart of `useAsyncPaginatedData`, which shows one page at a time. `fn` is stored in a
 * ref, so callers do not need to memoize it.
 */
export function useAsyncInfiniteData<T>(
  fn: (page: { limit: number; offset: number }) => Promise<PageResponse<T>>,
  options: UseAsyncInfiniteDataOptions = {}
): UseAsyncInfiniteDataResult<T> {
  const { pageSize = 50, key } = options;
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const [further, setFurther] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  // A page that comes back after the list was cleared belongs to the old list and is dropped.
  const generation = useRef(0);
  const requesting = useRef(false);

  const first = useAsyncData(async () => {
    const page = await fnRef.current({ limit: pageSize, offset: 0 });
    setTotal(page.total);
    return page.items;
  });

  const items = useMemo(() => [...(first.data ?? []), ...further], [first.data, further]);

  const next = useAsyncHandler(async () => {
    const mine = generation.current;
    const page = await fnRef.current({ limit: pageSize, offset: items.length });
    if (mine !== generation.current) return;
    setFurther((current) => [...current, ...page.items]);
    setTotal(page.total);
  });

  const hasMore = items.length < total;
  const { handle: loadNext } = next;
  const onReachEnd = useCallback(() => {
    if (requesting.current || !hasMore) return;
    requesting.current = true;
    void loadNext().finally(() => {
      requesting.current = false;
    });
  }, [hasMore, loadNext]);

  const { refresh: refreshFirst } = first;
  const refresh = useCallback(() => {
    generation.current += 1;
    requesting.current = false;
    setFurther([]);
    setTotal(0);
    void refreshFirst();
  }, [refreshFirst]);

  const lastKey = useRef(key);
  useEffect(() => {
    if (lastKey.current === key) return;
    lastKey.current = key;
    refresh();
  }, [key, refresh]);

  return {
    items,
    loading: first.loading,
    error: first.error,
    refresh,
    scroll: { hasMore, loadingMore: next.loading, error: next.error, onReachEnd },
  };
}
